import { driver } from 'driver.js'
import { currentTab } from '~/stores/profile-settings'

export const hasDemoBeenSeen = useStorage('hasDemoBeenSeen', false)
export const useFakeData = ref(false)
export const isDemoAccount = ref(false)

export function useDriver() {
  const {
    isRecycler,
    user,
    isAdmin,
  } = storeToRefs(useAuthStore())
  const router = useRouter()
  const { t } = useI18n()

  // Navigation helper functions
  const createNavigation = {
    async goTo(path: string, moveNext = true, delay = 100) {
      await router.push(path)
      setTimeout(() => {
        if (moveNext) {
          driverObj.value.moveNext()
        }
        else {
          driverObj.value.movePrevious()
        }
      }, delay)
    },

    async goToNext(path: string, delay = 100) {
      return this.goTo(path, true, delay)
    },

    async goToPrevious(path: string, delay = 100) {
      return this.goTo(path, false, delay)
    },

    moveNext() {
      driverObj.value.moveNext()
    },

    movePrevious() {
      driverObj.value.movePrevious()
    },
  }

  // Step factory functions
  const createStep = {
    navigation(element: string, titleKey: string, descriptionKey: string, targetPath: string, options: any = {}) {
      return {
        element,
        popover: {
          title: t(titleKey),
          description: t(descriptionKey),
          onNextClick: () => createNavigation.goToNext(targetPath),
          ...options.popoverOverrides,
        },
        ...options,
      }
    },

    action(element: string, descriptionKey: string, options: any = {}) {
      return {
        element,
        popover: {
          description: t(descriptionKey),
          ...options.popoverOverrides,
        },
        ...options,
      }
    },

    detail(element: string, titleKey: string, descriptionKey: string, options: any = {}) {
      return {
        element,
        popover: {
          title: t(titleKey),
          description: t(descriptionKey),
          ...options.popoverOverrides,
        },
        ...options,
      }
    },
  }

  // Recycler step builders
  const createRecyclerSteps = () => {
    const steps = [
      // Profile section
      createStep.navigation(
        '#navbar-element-profile',
        'global.profile',
        'tour.profile_desc',
        '/profile-settings',
        {
          popoverOverrides: {
            onNextClick: async () => {
              await router.push('/profile-settings')
              currentTab.value = 1
              setTimeout(() => {
                driverObj.value.moveNext()
              }, 100)
            },
          },
        },
      ),

      createStep.action('#profile-td', 'tour.td_connect', {
        mobile: true,
        popoverOverrides: {
          title: 'TrackDéchets',
          onNextClick: async () => {
            if (isMobile.value)
              await createNavigation.goToNext('/pricing')
            else createNavigation.moveNext()
          },
        },
      }),

      // Pricing section
      createStep.navigation(
        '#navbar-element-prices',
        'tour.pricing_title',
        'tour.pricing_desc',
        '/pricing',
        {
          popoverOverrides: {
            onPrevClick: () => {
              currentTab.value = 1
              createNavigation.goToPrevious('/profile-settings')
            },
          },
        },
      ),

      createStep.action('#create-pricing-button', 'tour.create_pricing', {
        mobile: true,
        popoverOverrides: {
          onPrevClick: () => {
            createNavigation.goToPrevious('/profile-settings')
          },
        },
      }),

      createStep.action('#pricing-table', 'tour.pricing_table', {
        mobile: true,
        popoverOverrides: {
          onNextClick: async () => {
            if (isMobile.value)
              await createNavigation.goToNext('/collects/')
            else createNavigation.moveNext()
          },
        },
      }),

      // Collects section
      createStep.navigation(
        '#navbar-element-collects',
        'global.collects',
        'tour.collects_nav_desc',
        '/collects/',
      ),

      createStep.action('#collects-recycler-table', 'tour.collect_desc', {
        mobile: true,
        popoverOverrides: {
          onNextClick: () => createNavigation.goToNext('/collects/1030'),
          onPrevClick: () => createNavigation.goToPrevious('/pricing'),
        },
      }),

      createStep.action('#collect-status-flow-wrapper', 'tour.follow_collect_status', {
        mobile: true,
        popoverOverrides: { onPrevClick: () => createNavigation.goToPrevious('/collects') },
      }),

      createStep.action('#collect-details', 'tour.collect_details', { mobile: true }),

      createStep.action('#add-collect-to-calendar', 'tour.collect_add_to_calendar', { mobile: true }),

      createStep.action('#collect-bsd-section', 'tour.view_bsd', {
        mobile: true,
        popoverOverrides: { onNextClick: () => createNavigation.goToNext('/my-documents') },
      }),

      // Documents section
      createStep.navigation(
        '#navbar-element-documents',
        'tour.documents_title',
        'tour.documents_nav_desc',
        '/my-documents/',
        { popoverOverrides: { onPrevClick: () => createNavigation.goToPrevious('/collects/1030') } },
      ),

      createStep.navigation('#document-recycler-card', 'tour.documents_title', 'tour.document-recycler-card', '/my-documents/subtypes?subtypes=co2_report,destruction_certificate,invoice,weighting_ticket,purchase_order,uncategorized,td_bsd&title=', { popoverOverrides: { onPrevClick: () => createNavigation.goToPrevious('/collects/1030') } }),

      createStep.action('#documents-table', 'tour.documents_desc', {
        mobile: true,
        popoverOverrides: {
          onNextClick: () => createNavigation.goToNext('/', 200),
          onPrevClick: async () => {
            await createNavigation.goToPrevious('/my-documents/')
          },
        },
      }),

      // Dashboard
      createStep.action('#dashboard', 'tour.dashboard', {
        mobile: true,
        popoverOverrides: { onPrevClick: () => createNavigation.goToPrevious('/my-documents/subtypes?subtypes=invoice,legacy_invoice,purchase_order&title=documents.invoice') },
      }),
    ]

    return isMobile.value ? steps.filter(step => step.mobile !== false) : steps
  }

  // Producer step builders
  const createProducerSteps = () => {
    const steps = [
      // Profile section
      createStep.navigation(
        '#navbar-element-profile',
        'global.profile',
        'tour.profile_desc',
        '/profile-settings',
        {
          popoverOverrides: {
            onNextClick: async () => {
              await router.push('/profile-settings')
              currentTab.value = 1
              setTimeout(() => {
                driverObj.value.moveNext()
              }, 100)
            },
          },
        },
      ),

      createStep.action('#profile-td', 'tour.td_connect', {
        mobile: true,
        popoverOverrides: {
          title: 'TrackDéchets',
          onNextClick: () => {
            if (isMobile.value) {
              setTimeout(() => createNavigation.goToNext('/compliances'), 100)
            }
            else {
              createNavigation.moveNext()
            }
          },
        },
      }),

      // Compliance section
      createStep.navigation(
        '#navbar-element-compliances',
        'tour.compliance_title',
        'tour.compliance_nav_desc',
        '/compliances',
        {
          mobile: false,
          popoverOverrides: {
            onNextClick: () => setTimeout(() => createNavigation.goToNext('/compliances'), 100),
            onPrevClick: () => {
              currentTab.value = 1
              createNavigation.goToPrevious('/profile-settings')
            },
          },
        },
      ),

      createStep.action('#compliance', 'tour.compliance_desc', {
        mobile: true,
        popoverOverrides: {
          onNextClick: async () => {
            if (isMobile.value)
              await router.push('/my-materials')
            createNavigation.moveNext()
          },
          onPrevClick: async () => {
            await createNavigation.goToPrevious('/profile-settings')
          },
        },
      }),

      // Materials section
      createStep.navigation(
        '#navbar-element-materials',
        'tour.your_waste',
        'tour.materials_desc',
        '/my-materials',
      ),

      createStep.action('#my-materials-table', 'tour.materials_desc2', {
        mobile: true,
        popoverOverrides: {
          onNextClick: async () => {
            if (isMobile.value)
              await createNavigation.goToNext('/collects/', 200)
            else createNavigation.moveNext()
          },
          onPrevClick: () => createNavigation.goToPrevious('/compliances'),
        },
      }),

      // Collects section
      createStep.detail(
        '#navbar-element-collects',
        'global.collects',
        'tour.collect_desc2',
        { popoverOverrides: { onNextClick: () => createNavigation.goToNext('/collects/') } },
      ),

      createStep.action('#create-collect-button', 'tour.create_collect', {
        mobile: true,
        popoverOverrides: { onPrevClick: () => createNavigation.goToPrevious('/my-materials') },
      }),

      createStep.action('#collects-producer-table', 'tour.wp_collect_desc', {
        mobile: true,
        popoverOverrides: {
          onNextClick: async () => {
            if (isMobile.value)
              await createNavigation.goToNext('/my-documents')
            else createNavigation.moveNext()
          },
        },
      }),

      // Documents section
      createStep.navigation(
        '#navbar-element-documents',
        'tour.documents_title',
        'tour.documents_nav_desc',
        '/my-documents',
        { popoverOverrides: { onPrevClick: () => createNavigation.goToPrevious('/collects/') } },
      ),

      createStep.action('#document-invoice-card', 'tour.documents_desc', {
        popoverOverrides: {
          onNextClick: () => createNavigation.goToNext('/my-documents/subtypes?subtypes=invoice,legacy_invoice,purchase_order&title=documents.invoice'),
          onPrevClick: async () => {
            if (isMobile.value)
              await createNavigation.goToPrevious('/')
            else createNavigation.movePrevious()
          },
        },
      }),

      createStep.action('#documents-table', 'tour.see_your_documents', {
        popoverOverrides: {
          onNextClick: () => createNavigation.goToNext('/reports/'),
          onPrevClick: () => createNavigation.goToPrevious('/my-documents/'),
        },
      }),

      createStep.action('#reports-content', 'tour.report_desc', {
        mobile: true,
        popoverOverrides: {
          onNextClick: () => createNavigation.goToNext('/', 200),
          onPrevClick: async () => {
            await createNavigation.goToPrevious('/my-documents/subtypes?subtypes=invoice,legacy_invoice,purchase_order&title=documents.invoice')
          },
        },
      }),

      // Dashboard
      createStep.action('#dashboard', 'tour.dashboard', {
        mobile: true,
        popoverOverrides: { onPrevClick: () => createNavigation.goToPrevious('/reports/') },
      }),
    ]

    return isMobile.value ? steps.filter(step => step.mobile !== false) : steps
  }

  const steps = computed(() => {
    return isRecycler.value ? createRecyclerSteps() : createProducerSteps()
  })

  const driverObj = computed(() => driver({
    showProgress: true,
    smoothScroll: true,
    onPopoverRender(popover) {
      popover.nextButton.classList.add('!btn-primary')
      popover.nextButton.classList.add('!btn')
      popover.nextButton.classList.add('!btn-sm')
      popover.nextButton.style.textShadow = 'none'

      popover.previousButton.classList.add('!btn')
      popover.previousButton.classList.add('!btn-sm')
      popover.previousButton.style.textShadow = 'none'
    },
    steps: steps.value,
    onDestroyed() {
      hasDemoBeenSeen.value = true
      stopDriver()
    },
  }))

  function startDriver(startDemo: boolean = false) {
    if (!featureFlags.guidedTour.value)
      return

    if (isAdmin.value || isMobile.value)
      return
    isDemoAccount.value = startDemo
    // Launch demo if the user has not seen it or if it is a demo account
    if (!hasDemoBeenSeen.value || startDemo) {
      useFakeData.value = true
      setTimeout(() => {
        driverObj.value.drive()
      }, 500)
    }
  }

  function stopDriver() {
    driverObj.value.destroy()
    if (user.value.user.email === 'demowr@cinqo.app' || user.value.user.email === 'demowp@cinqo.app') {
      router.push('/auth/login')
    }
    else {
      location.reload()
    }
    useFakeData.value = false
    isDemoAccount.value = false
  }

  return {
    driverObj,
    startDriver,
    stopDriver,
  }
}
